import { useEffect, useRef, useState } from 'react';

/**
 * Runs `fn` now and every `ms`, never overlapping, keeping the last good value on errors.
 * `deps` restart the loop (and clear the value) when they change; `null` fn pauses it.
 */
export function usePoll<T>(fn: (() => Promise<T>) | null, ms: number, deps: unknown[]): { data?: T; error?: string; refresh: () => void } {
  const [data, setData] = useState<T>();
  const [error, setError] = useState<string>();
  const kick = useRef<() => void>(() => {});

  useEffect(() => {
    setData(undefined);
    setError(undefined);
    if (!fn) return;
    let alive = true;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let running = false;
    const tick = async () => {
      if (running) return;
      running = true;
      clearTimeout(timer);
      try {
        const v = await fn();
        if (alive) {
          setData(v);
          setError(undefined);
        }
      } catch (e) {
        if (alive) setError(e instanceof Error ? e.message : String(e));
      } finally {
        running = false;
        if (alive) timer = setTimeout(tick, ms);
      }
    };
    kick.current = () => void tick();
    void tick();
    return () => {
      alive = false;
      clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  return { data, error, refresh: () => kick.current() };
}
